import { useEffect } from 'react';
import { getSlug } from '@lib/utils';
import { pushDataLayerEvent } from '@lib/gtm';
import { useRouter } from 'next/router';
import { useUserInfo, useSaved } from '@lib/user-context';

import Modal from '@components/modal';
import NotificationCard from '@components/notification-card';
import NotesCardModal from '@components/notes/notes-card-modal';
import ShareModal from '@components/modal/share-modal';
import RequestATourModal from '@components/request-a-tour-modal/component';
import OpenHouseModal from '@components/open-house-modal';
import ContactForm from '@components/form';
import { useListingContext } from '../../context';

import {
  notification_modal,
  notification_header,
  notification_card,
  browser_fix,
  notes_modal,
  preapproval,
  preapproval_header
} from './styles.module.scss';

const PropertyModals = ({
  shareModalIsOpen,
  setShareModalIsOpen,
  preApprovalModalIsOpen,
  setPreApprovalModalIsOpen,
  notesModalIsOpen,
  setNotesModalIsOpen,
  notificationModalIsOpen,
  setNotificationModalIsOpen,
  requestATourModalIsOpen,
  setRequestATourModalIsOpen,
  openHouseModalIsOpen,
  setOpenHouseModalIsOpen,
  updateNoteListings
}) => {
  const router = useRouter();
  const { userInfo } = useUserInfo();
  const { saved } = useSaved();

  const {
    openHouses,
    listingId: id,
    informationRequestDefaultComments,
    address,
    city,
    state,
    zip,
    photos,
    canonicalUrl,
    hasMapLocationExternalLink,
    mapLocationExternalLink,
    sharingServicesCollection,
    priceFormatted,
    displayAgents
  } = useListingContext();

  const savedListing =
    saved && saved.length > 0 ? saved.find(({ listingId }) => listingId === id) : null;

  const hasOpenHouse = openHouses[0];

  const selectedNotifications = [
    'shouldNotifyOnPriceChange',
    'shouldNotifyOnStatusChange',
    'shouldNotifyOnOpenHouseScheduled'
  ].filter((el) => savedListing?.[el]);

  const onOpenHouseModalClose = () => {
    setOpenHouseModalIsOpen(false);
  };

  const onNotificationModalClose = () => {
    setNotificationModalIsOpen(false);
  };

  const onNotesModalClose = () => {
    setNotesModalIsOpen(false);
  };

  const notificationModalContent = (
    <div className={notification_modal}>
      <div className={notification_header}>
        <div className={browser_fix}>Set up N</div>
        <div className={browser_fix}>otifications!</div>
        <div>Don't miss out on this property</div>
      </div>
      <div className={notification_card}>
        <NotificationCard
          savedListingId={savedListing?.savedListingId}
          button={{
            label: 'Update Notifications'
          }}
          selectedNotifications={selectedNotifications || []}
          checkboxes={{
            items: [
              {
                key: 'priceChange',
                label: 'Price change',
                value: 'shouldNotifyOnPriceChange'
              },
              {
                key: 'statusChange',
                label: 'Status change',
                value: 'shouldNotifyOnStatusChange'
              },
              {
                key: 'openHouse',
                label: 'An open house scheduled',
                value: 'shouldNotifyOnOpenHouseScheduled'
              }
            ],
            selectAllLabel: null
          }}
          propertyNotificationModalIsOpen={notificationModalIsOpen}
          setPropertyNotificationModalIsOpen={setNotificationModalIsOpen}
          heading="Notify me when this listing has:"
          image={photos[0]?.midSizeImageUrl}
          property={{
            address: address,
            city: city,
            listingId: id,
            priceFormatted: String(priceFormatted || ''),
            state: state,
            zip: zip
          }}
          listingId={id}
          listingUrl={`/listing/${id}`}
          url={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        />
      </div>
    </div>
  );

  const preApprovalModalContent = (
    <div className={preapproval}>
      <div className={preapproval_header}>
        <h2>Let's Move!</h2>
        <p>
          One of our loan officers will be in touch with you shortly to accelerate your
          pre-approval.
        </p>
      </div>
      <ContactForm
        agentIds={[]}
        submitLabel="Submit"
        requestType="Pre-Approval"
        theme="no padding"
        listingId={id}
        defaultCommentValue={`I would like to be pre-approved for the property at ${address}. Please contact me with next steps.`}
        extraRecipients={['Mortgage Leads <scmsleads@sibcycline.com>']}
        recipientNotification={
          displayAgents[0] &&
          `Once processed, please pass this lead to ${displayAgents[0].displayPrimary}`
        }
        shouldAllowLeadsPlusAgent
      ></ContactForm>
    </div>
  );

  const notesModalContent = (
    <div className={notes_modal}>
      <NotesCardModal
        image={photos[0]?.midSizeImageUrl}
        listingId={id}
        url={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        listingUrl={`/listing/${id}`}
        property={{
          address,
          city,
          priceFormatted: String(priceFormatted || ''),
          state,
          zip
        }}
        updateNoteListings={updateNoteListings}
        modalIsOpen={notesModalIsOpen}
        setModalIsOpen={setNotesModalIsOpen}
        updateParent={false}
      />
    </div>
  );

  useEffect(() => {
    if (shareModalIsOpen) {
      pushDataLayerEvent('listing_share_modal_click', router.asPath);
    }
    if (preApprovalModalIsOpen) {
      pushDataLayerEvent('listing_pre_approval_modal_click', router.asPath);
    }
    if (notesModalIsOpen) {
      pushDataLayerEvent('listing_notes_modal_click', router.asPath);
    }
    if (notificationModalIsOpen) {
      pushDataLayerEvent('listing_notification_modal_click', router.asPath);
    }
    if (requestATourModalIsOpen) {
      pushDataLayerEvent('listing_request_a_tour_modal_click', router.asPath);
    }
    if (openHouseModalIsOpen) {
      pushDataLayerEvent('listing_open_house_modal_click', router.asPath);
    }
  }, [
    shareModalIsOpen,
    preApprovalModalIsOpen,
    notesModalIsOpen,
    notificationModalIsOpen,
    requestATourModalIsOpen,
    openHouseModalIsOpen
  ]);

  const getAgentIdForTourModal = () => {
    if (router.query.tourModal !== 'true' && router.query.tourModal) {
      return parseInt(router.query.tourModal);
    } else {
      return displayAgents[0]?.agentId || null;
    }
  };

  return (
    <>
      {shareModalIsOpen && (
        <ShareModal
          modalIsOpen={shareModalIsOpen}
          onModalClose={() => setShareModalIsOpen(false)}
          sharingServicesCollection={sharingServicesCollection}
          copyUrl={canonicalUrl}
        />
      )}

      {preApprovalModalIsOpen && (
        <Modal
          modalIsOpen={preApprovalModalIsOpen}
          onModalClose={() => setPreApprovalModalIsOpen(false)}
          content={preApprovalModalContent}
          theme="pre-approval"
        />
      )}

      {notesModalIsOpen && (
        <Modal
          modalIsOpen={notesModalIsOpen}
          onModalClose={onNotesModalClose}
          content={notesModalContent}
          theme="notes"
        />
      )}

      {notificationModalIsOpen && (
        <Modal
          modalIsOpen={notificationModalIsOpen}
          onModalClose={onNotificationModalClose}
          content={notificationModalContent}
        />
      )}

      {requestATourModalIsOpen && (
        <RequestATourModal
          listingId={id}
          agentId={getAgentIdForTourModal()}
          modalIsOpen={requestATourModalIsOpen}
          setModalIsOpen={setRequestATourModalIsOpen}
          defaultFormValue={informationRequestDefaultComments}
          userInfo={userInfo}
          address={address}
          city={city}
          state={state}
          zip={zip}
          photos={photos}
          listingUrl={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        />
      )}

      {openHouseModalIsOpen && hasOpenHouse && (
        <OpenHouseModal
          modalIsOpen={openHouseModalIsOpen}
          setModalIsOpen={onOpenHouseModalClose}
          agentId={router?.query?.openhouse}
          listingId={id}
          userInfo={userInfo}
          address={address}
          city={city}
          state={state}
          zip={zip}
          photos={photos}
          listingUrl={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
          openHouses={openHouses}
          directionsUrl={hasMapLocationExternalLink ? mapLocationExternalLink : null}
        />
      )}
    </>
  );
};

export default PropertyModals;
